<template>
  <div class="main container-fluid profile-home-page">
    <header class="profile-home__header">
      <div>
        <h4 class="profile-home__title mb-1">Профиль</h4>
        <p class="profile-home__subtitle mb-0">Управление безопасностью аккаунта и интеграциями</p>
      </div>
    </header>

    <section class="profile-home__hero px-3">
      <div class="profile-home__hero-card">
        <div class="profile-home__hero-bg"></div>
        <div class="profile-home__hero-content">
          <div class="profile-home__identity">
            <div class="profile-home__avatar">
              <i class="bi bi-person-circle"></i>
            </div>
            <div>
              <small class="profile-home__identity-label">Пользователь</small>
              <div class="profile-home__identity-value">{{ currentUserLabel }}</div>
            </div>
          </div>

          <div class="profile-home__actions">
            <router-link :to="{ name: 'ProfilePassword' }" class="profile-action-card profile-action-card--security">
              <div class="profile-action-card__icon"><i class="bi bi-shield-lock-fill"></i></div>
              <div>
                <div class="profile-action-card__title">Смена пароля</div>
                <div class="profile-action-card__description">Обновите пароль для входа в систему</div>
              </div>
              <i class="bi bi-arrow-right-short profile-action-card__arrow"></i>
            </router-link>

            <router-link :to="{ name: 'ProfileCredentials' }" class="profile-action-card profile-action-card--credentials">
              <div class="profile-action-card__icon"><i class="bi bi-key-fill"></i></div>
              <div>
                <div class="profile-action-card__title">Интеграционные пароли</div>
                <div class="profile-action-card__description">Управляйте credential для WordPress и ботов</div>
              </div>
              <i class="bi bi-arrow-right-short profile-action-card__arrow"></i>
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <section class="px-3 pb-3">
      <div class="profile-home__stats-card">
        <div class="profile-home__stats-header d-flex align-items-center justify-content-between gap-2">
          <div>
            <h6 class="mb-0">Краткая статистика</h6>
            <small class="text-muted">{{ statsPeriodLabel }}</small>
          </div>
        </div>

        <div v-if="statsError" class="alert alert-danger py-2 px-3 mt-3 mb-0">{{ statsError }}</div>
        <div v-else-if="statsLoading" class="profile-home__loading text-muted small mt-3">Загрузка статистики...</div>

        <div v-else class="row g-2 mt-1">
          <div class="col-6 col-lg-4" v-for="item in statsCards" :key="item.key">
            <article class="profile-home-stat">
              <div class="profile-home-stat__label">{{ item.label }}</div>
              <div class="profile-home-stat__value">{{ item.value }}</div>
            </article>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import axios from 'axios'
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

const store = useStore()
const numberFormatter = new Intl.NumberFormat('ru-RU')
const usdFormatter = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'USD' })

const statsLoading = ref(false)
const statsError = ref('')
const stats = ref(null)

const currentUserLabel = computed(() => {
  const user = store.getters.currentUser
  return String(user?.email || user?.username || 'Неизвестный пользователь')
})

const statsPeriodLabel = computed(() => {
  const from = stats.value?.fromDate
  const to = stats.value?.toDate
  if (!from || !to) return 'Период: —'
  return `Период: ${from} - ${to}`
})

const statsCards = computed(() => {
  const aiTotals = stats.value?.ai?.totals || {}
  const publishedContent = stats.value?.publishedContent || {}
  const spend = stats.value?.spend || {}

  const activeStreamsIds = new Set()
  const collect = arr => {
    if (!Array.isArray(arr)) return
    arr.forEach(x => activeStreamsIds.add(String(x.streamId)))
  }

  collect(spend.byStream)
  collect(stats.value?.publishedContent?.byStreams)

  return [
    { key: 'spendTotal', label: 'Стоимость всего ($)', value: formatUsd(spend.totalUsd) },
    { key: 'spendLlm', label: 'Стоимость LLM ($)', value: formatUsd(spend.directLlmUsd) },
    { key: 'spendCmaker', label: 'Стоимость cmaker ($)', value: formatUsd(spend.cmakerUsd) },
    { key: 'aiRequests', label: 'AI запросы', value: formatNumber(aiTotals.requestsCount) },
    { key: 'published', label: 'Публикации', value: formatNumber(publishedContent.publishedCount) },
    { key: 'activeStreams', label: 'Активные стримы', value: formatNumber(activeStreamsIds.size) },
  ]
})

function formatNumber(value) {
  return numberFormatter.format(Number(value || 0))
}

function formatUsd(value) {
  return usdFormatter.format(Number(value || 0))
}

function extractErrorMessage(error, fallback) {
  const message = error?.response?.data?.message || error?.response?.data?.error || error?.response?.data?.data
  return String(message || fallback)
}

async function loadQuickStats() {
  statsLoading.value = true
  statsError.value = ''
  try {
    const { data } = await axios.get('/api/user-statistics')
    stats.value = data || {}
  } catch (error) {
    statsError.value = extractErrorMessage(error, 'Не удалось загрузить статистику пользователя.')
    stats.value = null
  } finally {
    statsLoading.value = false
  }
}

onMounted(async () => {
  await loadQuickStats()
})
</script>

<style scoped>
.profile-home-page {
  min-height: 100%;
  padding-top: 10px;
  background:
    radial-gradient(circle at 2% 3%, rgba(66, 153, 225, 0.2), transparent 36%),
    radial-gradient(circle at 98% 2%, rgba(56, 189, 248, 0.18), transparent 32%),
    #f3f7ff;
}

.profile-home__header {
  margin: 0 20px 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: linear-gradient(135deg, #10305f 0%, #1b4f93 60%, #1e67b7 100%);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 18px 40px rgba(16, 48, 95, 0.22);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.profile-home__title {
  font-weight: 800;
  letter-spacing: 0.01em;
}

.profile-home__subtitle {
  color: rgba(232, 242, 255, 0.92);
  font-size: 13px;
}

.profile-home__hero-card {
  position: relative;
  border-radius: 16px;
  border: 1px solid #d6e4fb;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 12px 32px rgba(31, 54, 92, 0.1);
}

.profile-home__hero-bg {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(120deg, rgba(11, 77, 162, 0.11) 0%, rgba(19, 124, 186, 0.08) 38%, rgba(255, 255, 255, 0) 100%),
    repeating-linear-gradient(140deg, rgba(15, 42, 89, 0.04) 0 9px, transparent 9px 18px);
}

.profile-home__hero-content {
  position: relative;
  z-index: 1;
  padding: 16px;
}

.profile-home__identity {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.profile-home__avatar {
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: linear-gradient(135deg, #155bb0 0%, #1f7dd0 100%);
  color: #e9f5ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  box-shadow: 0 8px 18px rgba(25, 88, 168, 0.25);
}

.profile-home__identity-label {
  display: block;
  color: #57739d;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 11px;
  font-weight: 700;
}

.profile-home__identity-value {
  color: #1f3659;
  font-weight: 700;
  font-size: 15px;
}

.profile-home__actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.profile-action-card {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 10px;
  border-radius: 14px;
  border: 1px solid #d7e4fa;
  background: #f7fbff;
  text-decoration: none;
  padding: 12px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.profile-action-card:hover {
  transform: translateY(-1px);
  border-color: #9fbeee;
  box-shadow: 0 14px 26px rgba(31, 77, 138, 0.16);
}

.profile-action-card__icon {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #fff;
}

.profile-action-card--security .profile-action-card__icon {
  background: linear-gradient(135deg, #0e6380 0%, #1f8ea8 100%);
}

.profile-action-card--credentials .profile-action-card__icon {
  background: linear-gradient(135deg, #205ea8 0%, #2b86d4 100%);
}

.profile-action-card__title {
  color: #214168;
  font-weight: 700;
  font-size: 14px;
}

.profile-action-card__description {
  color: #607ca5;
  font-size: 12px;
  margin-top: 1px;
}

.profile-action-card__arrow {
  font-size: 22px;
  color: #3f6fa8;
}

.profile-home__stats-card {
  margin-top: 12px;
  border: 1px solid #dbe6f8;
  border-radius: 16px;
  background: #fff;
  padding: 14px;
}

.profile-home__stats-header h6 {
  color: #1f3659;
  font-weight: 700;
}

.profile-home-stat {
  border: 1px solid #e4ecfb;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  min-height: 88px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.profile-home-stat__label {
  color: #5d7396;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 700;
}

.profile-home-stat__value {
  margin-top: 3px;
  color: #233f67;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.15;
}

@media (max-width: 992px) {
  .profile-home__header {
    margin: 0 12px 12px;
    padding: 12px;
  }

  .profile-home__actions {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .profile-home__header {
    padding: 11px 12px;
  }
}
</style>
